"use client";

import React, { useState, useTransition } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  ArrowUpRight,
  Check,
  X,
  TrendingUp,
  Brain,
  Shield,
  Clock,
  Briefcase,
  AlertTriangle,
} from "lucide-react";
import Link from "next/link";
import { updateProposalStatusAction } from "@/actions/shadow-board-actions";

export interface ProposalSandboxState {
  budgetMultiplier: number;
  workloadAllocation: number;
  opexSavings: number;
  newHires: number;
}

export interface Proposal {
  id: string;
  title: string;
  description: string;
  category: string;
  riskAppetite: "low" | "medium" | "high";
  impactValue: string;
  sandboxState: ProposalSandboxState;
  status: "pending" | "applied" | "rejected";
  createdAt?: string;
  updatedAt?: string;
}

interface ProposalCardProps {
  proposal: Proposal;
  onStatusChange?: (
    id: string,
    newStatus: "pending" | "applied" | "rejected",
  ) => void;
}

export function ProposalCard({ proposal, onStatusChange }: ProposalCardProps) {
  const [isPending, startTransition] = useTransition();
  const [localStatus, setLocalStatus] = useState<
    "pending" | "applied" | "rejected"
  >(proposal.status);
  const [errorMsg, setErrorMsg] = useState("");

  const handleUpdateStatus = async (
    status: "pending" | "applied" | "rejected",
  ) => {
    setErrorMsg("");
    startTransition(async () => {
      try {
        const res = await updateProposalStatusAction(proposal.id, status);
        if (res?.success) {
          setLocalStatus(status);
          if (onStatusChange) {
            onStatusChange(proposal.id, status);
          }
        } else {
          setErrorMsg("Impossibile aggiornare la proposta.");
        }
      } catch (err: any) {
        setErrorMsg("Errore di connessione.");
      }
    });
  };

  const getRiskBadgeColor = (risk: string) => {
    switch (risk) {
      case "low":
        return "success";
      case "high":
        return "error";
      default:
        return "warning";
    }
  };

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case "finance":
        return <TrendingUp className="h-4 w-4 text-category-finance" />;
      case "hr":
        return <Brain className="h-4 w-4 text-category-people" />;
      default:
        return <Briefcase className="h-4 w-4 text-category-operations" />;
    }
  };

  if (localStatus !== "pending") {
    return (
      <Card className="opacity-60 transition-all border-dashed border-border-strong/50">
        <CardContent className="flex flex-col items-center justify-center p-6 text-center space-y-3">
          {localStatus === "applied" ? (
            <div className="h-10 w-10 rounded-full bg-success-soft flex items-center justify-center text-success border border-success/30">
              <Check className="h-5 w-5" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded-full bg-error-soft flex items-center justify-center text-error border border-error/30">
              <X className="h-5 w-5" />
            </div>
          )}
          <div>
            <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Proposta {localStatus === "applied" ? "Approvata" : "Rifiutata"}
            </h4>
            <p className="text-[11px] text-text-secondary mt-1 max-w-xs mx-auto">
              "{proposal.title}" è stata contrassegnata come{" "}
              {localStatus === "applied" ? "applicata" : "rifiutata"} nel
              sistema.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[10px] text-text-muted hover:text-text-primary"
            onClick={() => handleUpdateStatus("pending")}
            disabled={isPending}
          >
            Annulla
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Construct Sandbox simulation Link query params
  const sandboxLink = `/strategy/sandbox?budgetMultiplier=${proposal.sandboxState.budgetMultiplier}&workloadAllocation=${proposal.sandboxState.workloadAllocation}&opexSavings=${proposal.sandboxState.opexSavings}&newHires=${proposal.sandboxState.newHires}`;

  return (
    <Card className="flex flex-col justify-between overflow-hidden relative border-l-4 border-l-primary-500 shadow-sm hover:shadow-md transition-all">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-bold text-text-secondary uppercase tracking-wide">
            {getCategoryIcon(proposal.category)}
            <span>{proposal.category}</span>
          </div>
          <div className="flex gap-1">
            <Badge variant={proposal.category as any}>
              {proposal.category}
            </Badge>
            <Badge variant={getRiskBadgeColor(proposal.riskAppetite)}>
              Rischio{" "}
              {proposal.riskAppetite === "low"
                ? "Basso"
                : proposal.riskAppetite === "high"
                  ? "Alto"
                  : "Medio"}
            </Badge>
          </div>
        </div>
        <CardTitle className="text-sm font-bold text-text-primary mt-2 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary-500 shrink-0" />
          {proposal.title}
        </CardTitle>
        <CardDescription className="text-xs text-text-secondary leading-relaxed mt-1">
          {proposal.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="py-2">
        <div className="bg-primary-500/5 border border-primary-500/10 p-3 rounded-lg flex items-center justify-between text-xs mt-1">
          <div className="space-y-0.5">
            <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider block">
              Impatto Stimato AI
            </span>
            <span className="font-extrabold text-primary-600 text-sm">
              {proposal.impactValue}
            </span>
          </div>
          <div className="text-right text-[10px] text-text-muted space-y-0.5">
            <span className="block">
              Budget: {proposal.sandboxState.budgetMultiplier.toFixed(1)}x
            </span>
            <span className="block">
              Team: {proposal.sandboxState.workloadAllocation}%
            </span>
          </div>
        </div>
        {errorMsg && (
          <p className="text-[11px] text-error mt-2 font-medium">{errorMsg}</p>
        )}
      </CardContent>

      <CardFooter className="pt-3 border-t border-border/20 mt-3 flex items-center justify-between bg-card-muted/10 px-6 py-3 rounded-b-xl flex-wrap gap-2">
        <Link href={sandboxLink} className="inline-flex">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1 border-primary-500/20 text-primary-600 hover:bg-primary-500/5 cursor-pointer"
          >
            Simula in Sandbox
            <ArrowUpRight className="h-3 w-3" />
          </Button>
        </Link>

        <div className="flex items-center gap-1.5 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-text-secondary hover:text-error hover:bg-error-soft/30 rounded-full cursor-pointer"
            onClick={() => handleUpdateStatus("rejected")}
            disabled={isPending}
            title="Rifiuta proposta"
          >
            <X className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-text-secondary hover:text-success hover:bg-success-soft/30 rounded-full cursor-pointer"
            onClick={() => handleUpdateStatus("applied")}
            disabled={isPending}
            title="Applica proposta"
          >
            <Check className="h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
